import { createTheme, alpha } from "@mui/material/styles";

// ─── Nepali flag-inspired palette ───────────────────────────────────────────
// The Nepali national flag is crimson red with a deep blue border and features
// the moon and sun in white. We extend this into a modern, high-contrast palette.

const CRIMSON  = "#B91C2C"; // deep crimson (flag red)
const NAVY     = "#0F2156"; // deep navy   (flag border)
const GOLD     = "#E8A020"; // warm gold   (sun on flag)
const CRIMSON_LIGHT = "#DC2626";
const NAVY_LIGHT    = "#1E3A8A";

// Shared subtle tint used on every card's own background — reused directly
// (not just via MuiCard) wherever a section should visually stay part of the
// same card, e.g. an expanded comments panel.
const CARD_TINT_GRADIENT = `linear-gradient(135deg, ${alpha(NAVY, 0.1)} 0%, #FFFFFF 45%, ${alpha(GOLD, 0.1)} 100%)`;

// Shared page-background wash — reused across the main content area, the
// drawer, and any other full-page surface (e.g. the message thread) so the
// whole app carries one consistent brand gradient, not just cards.
const PAGE_BG_GRADIENT = `linear-gradient(165deg, ${alpha(NAVY, 0.32)} 0%, ${alpha(NAVY, 0.12)} 20%, ${alpha("#F5F3F0", 1)} 45%, ${alpha(GOLD, 0.3)} 75%, ${alpha(CRIMSON, 0.32)} 100%)`;

const theme = createTheme({
  palette: {
    primary: {
      main: CRIMSON,
      light: CRIMSON_LIGHT,
      dark: "#8B0F1B",
      contrastText: "#ffffff",
    },
    secondary: {
      main: NAVY,
      light: NAVY_LIGHT,
      dark: "#081540",
      contrastText: "#ffffff",
    },
    warning: {
      main: GOLD,
      light: "#F59E0B",
      dark: "#B45309",
      contrastText: "#ffffff",
    },
    background: {
      default: "#F5F3F0",   // warm off-white — avoids clinical cold grey
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A1A2E",
      secondary: "#6B7280",
    },
    divider: "#E5E7EB",
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800, letterSpacing: "-0.02em" },
    h2: { fontWeight: 800, letterSpacing: "-0.02em" },
    h3: { fontWeight: 700, letterSpacing: "-0.01em" },
    h4: { fontWeight: 700, letterSpacing: "-0.01em" },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: "0.01em" },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1)",
    "0 1px 3px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.1)",
    "0 2px 6px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.1)",
    "0 4px 12px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.08)",
    "0 6px 16px rgba(0,0,0,0.1), 0 12px 32px rgba(0,0,0,0.08)",
    "0 8px 24px rgba(0,0,0,0.12)",
    "0 10px 28px rgba(0,0,0,0.12)",
    "0 12px 32px rgba(0,0,0,0.12)",
    "0 14px 36px rgba(0,0,0,0.12)",
    "0 16px 40px rgba(0,0,0,0.12)",
    "0 18px 44px rgba(0,0,0,0.12)",
    "0 20px 48px rgba(0,0,0,0.12)",
    "0 22px 52px rgba(0,0,0,0.12)",
    "0 24px 56px rgba(0,0,0,0.12)",
    "0 26px 60px rgba(0,0,0,0.12)",
    "0 28px 64px rgba(0,0,0,0.12)",
    "0 30px 68px rgba(0,0,0,0.12)",
    "0 32px 72px rgba(0,0,0,0.12)",
    "0 34px 76px rgba(0,0,0,0.12)",
    "0 36px 80px rgba(0,0,0,0.12)",
    "0 38px 84px rgba(0,0,0,0.12)",
    "0 40px 88px rgba(0,0,0,0.12)",
    "0 42px 92px rgba(0,0,0,0.12)",
    "0 44px 96px rgba(0,0,0,0.12)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 10,
          padding: "7px 18px",
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${CRIMSON_LIGHT} 0%, ${CRIMSON} 100%)`,
          boxShadow: `0 2px 8px ${alpha(CRIMSON, 0.35)}`,
          "&:hover": {
            background: `linear-gradient(135deg, ${CRIMSON} 0%, #8B0F1B 100%)`,
            boxShadow: `0 4px 16px ${alpha(CRIMSON, 0.45)}`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${NAVY_LIGHT} 0%, ${NAVY} 100%)`,
          boxShadow: `0 2px 8px ${alpha(NAVY, 0.35)}`,
          "&:hover": {
            background: `linear-gradient(135deg, ${NAVY} 0%, #081540 100%)`,
          },
        },
        outlinedPrimary: {
          borderColor: alpha(CRIMSON, 0.5),
          "&:hover": {
            borderColor: CRIMSON,
            bgcolor: alpha(CRIMSON, 0.05),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: CARD_TINT_GRADIENT,
          backgroundColor: "#FFFFFF",
          boxShadow: "none",
          borderRadius: 14,
          border: `1px solid ${alpha(NAVY, 0.09)}`,
          transition: "border-color 0.2s ease",
          "&:hover": {
            borderColor: alpha(CRIMSON, 0.25),
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: `0 2px 12px ${alpha(NAVY, 0.2)}`,
          backgroundImage: "none",
        },
        colorPrimary: {
          backgroundColor: NAVY,
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 58,
          backgroundColor: "transparent",
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: alpha("#FFFFFF", 0.65),
          "&.Mui-selected": {
            color: GOLD,
          },
          minWidth: 0,
          paddingTop: 8,
        },
        label: {
          fontSize: "0.65rem !important",
          "&.Mui-selected": {
            fontSize: "0.65rem !important",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
          fontSize: "0.7rem",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(CRIMSON, 0.5),
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: CRIMSON,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 14,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          mx: 0.5,
          "&.Mui-selected": {
            backgroundColor: alpha(CRIMSON, 0.08),
            color: CRIMSON,
            "& .MuiListItemIcon-root": { color: CRIMSON },
            "&:hover": { backgroundColor: alpha(CRIMSON, 0.12) },
          },
          "&:hover": {
            backgroundColor: alpha(NAVY, 0.06),
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${CRIMSON_LIGHT} 0%, ${CRIMSON} 100%)`,
          boxShadow: `0 4px 20px ${alpha(CRIMSON, 0.5)}`,
          "&:hover": {
            background: `linear-gradient(135deg, ${CRIMSON} 0%, #8B0F1B 100%)`,
            boxShadow: `0 6px 24px ${alpha(CRIMSON, 0.6)}`,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#F0EDEA",
        },
      },
    },
  },
});

export default theme;
export { CRIMSON, NAVY, GOLD, CARD_TINT_GRADIENT, PAGE_BG_GRADIENT };
